import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import * as XLSX from 'xlsx';
import { CSVTable } from './CSVTable';
import { downloadCSVTemplate } from './csvTemplateUtils';
import { ExampleTable } from './ExampleTable';

interface CSVAllocationData {
  isin: string;
  current_allocation: number;
  sector: string;
  ticker?: string;
  name?: string;
  _hasError?: boolean;
  _errorMessage?: string;
}

interface AllocationAnalysisDialogProps {
  visible: boolean;
  onHide: () => void;
  onDataProcessed?: (
    data: CSVAllocationData[],
    description: string,
    batchType: 'universe' | 'benchmark' | 'current_holdings' | 'proposed',
  ) => Promise<void> | void;
  /** If true, forces batch type to 'current_holdings' and disables the dropdown */
  forceCurrentHoldings?: boolean;
  /** Initial batch type to use (defaults to 'current_holdings') */
  initialBatchType?: 'universe' | 'benchmark' | 'current_holdings' | 'proposed';
}

interface ValidationWarning {
  type: 'warning' | 'error';
  message: string;
}

const normalizeSectorValue = (sector: string): string => {
  const normalized = sector.trim().toLowerCase();
  if (normalized === 'c' || normalized === 'd') {
    return normalized;
  }
  return '';
};

const removeTrailingBlankRows = (data: unknown[][]): unknown[][] => {
  let lastNonEmptyIndex = data.length - 1;

  while (lastNonEmptyIndex >= 0) {
    const row = data[lastNonEmptyIndex];
    const hasContent = row.some(
      (cell) => cell !== null && cell !== undefined && cell !== '',
    );

    if (hasContent) {
      break;
    }
    lastNonEmptyIndex--;
  }

  return data.slice(0, lastNonEmptyIndex + 1);
};

const validateHeaders = (headers: string[]): string | null => {
  const normalizedHeaders = headers.map((h) => h.toLowerCase().trim());
  const requiredHeaders = ['isin', 'current_allocation'];
  const validHeaders = [...requiredHeaders, 'sector', 'ticker', 'name'];

  for (const required of requiredHeaders) {
    if (!normalizedHeaders.includes(required)) {
      return `Missing required column: ${required}`;
    }
  }

  // Allow additional columns but warn about them
  const invalidHeaders = normalizedHeaders.filter(
    (h) => h && !validHeaders.includes(h),
  );
  if (invalidHeaders.length > 0) {
    console.warn(
      `Additional columns found (will be ignored): ${invalidHeaders.join(', ')}`,
    );
  }

  return null;
};

const validateAndMarkRows = (
  data: CSVAllocationData[],
): CSVAllocationData[] => {
  return data.map((row) => {
    const errors: string[] = [];

    if (!row.isin || row.isin.trim() === '') {
      errors.push('ISIN is empty');
    }

    if (isNaN(row.current_allocation) || row.current_allocation < 0) {
      errors.push(`Invalid allocation: ${row.current_allocation}`);
    }

    if (errors.length > 0) {
      return {
        ...row,
        _hasError: true,
        _errorMessage: errors.join('; '),
      };
    }

    return row;
  });
};

const validateTotalAllocation = (
  data: CSVAllocationData[],
): ValidationWarning | null => {
  const total = data.reduce(
    (sum, row) => sum + (row.current_allocation || 0),
    0,
  );
  const roundedTotal = Math.round(total * 100) / 100;

  if (roundedTotal > 100) {
    return {
      type: 'error',
      message: `Total allocation is ${roundedTotal.toFixed(2)}% which exceeds 100%. Please adjust your allocations.`,
    };
  }

  if (roundedTotal < 100) {
    return {
      type: 'warning',
      message: `Total allocation is ${roundedTotal.toFixed(2)}%.`,
    };
  }

  return null;
};

const validateDuplicateISINs = (
  data: CSVAllocationData[],
): {
  hasDuplicates: boolean;
  duplicateISINs: string[];
  validatedData: CSVAllocationData[];
} => {
  const isinCounts = new Map<string, number>();
  const duplicates = new Set<string>();

  // Count occurrences of each ISIN (case-insensitive)
  data.forEach((row) => {
    if (row.isin && row.isin.trim() !== '') {
      const normalizedISIN = row.isin.trim().toUpperCase();
      const count = isinCounts.get(normalizedISIN) || 0;
      isinCounts.set(normalizedISIN, count + 1);

      if (count > 0) {
        duplicates.add(normalizedISIN);
      }
    }
  });

  // Mark rows with duplicate ISINs
  const validatedData = data.map((row) => {
    const normalizedISIN = row.isin?.trim().toUpperCase();
    if (normalizedISIN && duplicates.has(normalizedISIN)) {
      const existingError = row._errorMessage || '';
      const duplicateError = 'Duplicate ISIN';
      const newError = existingError
        ? `${existingError}; ${duplicateError}`
        : duplicateError;

      return {
        ...row,
        _hasError: true,
        _errorMessage: newError,
      };
    }
    return row;
  });

  return {
    hasDuplicates: duplicates.size > 0,
    duplicateISINs: Array.from(duplicates),
    validatedData,
  };
};

const hasRowErrors = (data: CSVAllocationData[]): boolean => {
  return data.some((row) => row._hasError);
};

const parseCsvLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote inside quoted field
        currentValue += '"';
        i += 2;
        continue;
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes;
        i++;
        continue;
      }
    }

    if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue.trim());
      currentValue = '';
      i++;
      continue;
    }

    currentValue += char;
    i++;
  }

  // Push the last value
  values.push(currentValue.trim());

  return values;
};

const parseTabularData = (text: string): CSVAllocationData[] => {
  const lines = text.split('\n').filter((line) => line.trim() !== '');

  if (lines.length === 0) {
    throw new Error('No data found');
  }

  const firstLine = lines[0];
  const delimiter = firstLine.includes('	') ? '	' : ',';

  const headers = parseCsvLine(firstLine, delimiter).map((header) =>
    header.replace(/"/g, '').toLowerCase(),
  );

  const headerError = validateHeaders(headers);
  if (headerError) {
    throw new Error(headerError);
  }

  const data: CSVAllocationData[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const values = parseCsvLine(line, delimiter).map((value) =>
      value.replace(/"/g, ''),
    );

    const hasContent = values.some((v) => v !== '');
    if (!hasContent) continue;

    const isinIndex = headers.indexOf('isin');
    const currentAllocationIndex = headers.indexOf('current_allocation');
    const sectorIndex = headers.indexOf('sector');
    const tickerIndex = headers.indexOf('ticker');
    const nameIndex = headers.indexOf('name');

    const row: CSVAllocationData = {
      isin: values[isinIndex] || '',
      current_allocation: parseFloat(values[currentAllocationIndex]) || 0,
      sector: sectorIndex >= 0 ? normalizeSectorValue(values[sectorIndex]) : '',
      ticker: tickerIndex >= 0 ? values[tickerIndex].trim() : undefined,
      name: nameIndex >= 0 ? values[nameIndex].trim() : undefined,
    };

    data.push(row);
  }

  // Filter out rows with empty ISINs
  return data.filter((row) => row.isin && row.isin.trim() !== '');
};

const parseExcel = (file: File): Promise<CSVAllocationData[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        // Use cellStyles and cellText options to get full formatting information
        const workbook = XLSX.read(data, {
          type: 'array',
          cellFormula: true, // Parse formulas (populates cell.f)
          cellStyles: true, // Include cell styles
          cellText: true, // Include formatted text (cell.w)
          cellDates: true, // Parse dates
        });

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        const rawData: unknown[][] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        });
        const cleanedData = removeTrailingBlankRows(rawData);

        if (cleanedData.length === 0) {
          reject(new Error('No data found in Excel file'));
          return;
        }

        const headers = cleanedData[0].map((h: unknown) =>
          String(h || '')
            .toLowerCase()
            .trim(),
        );

        const headerError = validateHeaders(headers);
        if (headerError) {
          reject(new Error(headerError));
          return;
        }

        // Check if the allocation column has percentage formatting by examining a sample cell
        const currentAllocationIndex = headers.indexOf('current_allocation');
        const allocationColumnLetter = String.fromCharCode(
          65 + currentAllocationIndex,
        ); // Convert index to letter (0→A, 1→B, etc.)
        const sampleCell = worksheet[`${allocationColumnLetter}2`]; // Check first data row (row 1)
        const numFmt =
          sampleCell?.s?.numFmt || (sampleCell as { z?: string })?.z;
        const hasPercentageFormat =
          numFmt && typeof numFmt === 'string' && numFmt.includes('%');

        const parsedData: CSVAllocationData[] = [];

        for (let i = 1; i < cleanedData.length; i++) {
          const row = cleanedData[i];

          const hasContent = row.some(
            (cell: unknown) =>
              cell !== null && cell !== undefined && cell !== '',
          );
          if (!hasContent) continue;

          const isinIndex = headers.indexOf('isin');
          const currentAllocationIndex = headers.indexOf('current_allocation');
          const sectorIndex = headers.indexOf('sector');
          const tickerIndex = headers.indexOf('ticker');
          const nameIndex = headers.indexOf('name');

          const parsedAllocation =
            parseFloat(String(row[currentAllocationIndex] || '0')) || 0;

          // Only convert if the cell has percentage formatting AND the value is a decimal (0 < value < 1)
          // This prevents misinterpreting small percentages like 0.5% or 0.0124% as decimals that need conversion
          const finalAllocation =
            hasPercentageFormat && parsedAllocation > 0 && parsedAllocation < 1
              ? parsedAllocation * 100
              : parsedAllocation;

          const allocation = {
            isin: String(row[isinIndex] || '').trim(),
            current_allocation: finalAllocation,
            sector:
              sectorIndex >= 0
                ? normalizeSectorValue(String(row[sectorIndex] || ''))
                : '',
            ticker:
              tickerIndex >= 0
                ? String(row[tickerIndex] || '').trim()
                : undefined,
            name:
              nameIndex >= 0 ? String(row[nameIndex] || '').trim() : undefined,
          };

          parsedData.push(allocation);
        }

        resolve(parsedData);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsArrayBuffer(file);
  });
};

const parseCSV = (csvText: string): CSVAllocationData[] => {
  return parseTabularData(csvText);
};

export const AllocationAnalysisDialog: React.FC<
  AllocationAnalysisDialogProps
> = ({
  visible,
  onHide,
  onDataProcessed,
  forceCurrentHoldings = false,
  initialBatchType = 'current_holdings',
}) => {
  const [csvData, setCsvData] = useState<CSVAllocationData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [batchType, setBatchType] = useState<
    'universe' | 'benchmark' | 'current_holdings' | 'proposed'
  >(initialBatchType);

  // Force batch type to current_holdings when forceCurrentHoldings is true
  // Otherwise, sync with initialBatchType when dialog opens
  useEffect(() => {
    if (forceCurrentHoldings) {
      setBatchType('current_holdings');
    } else if (visible && initialBatchType) {
      // Update batch type when dialog opens to match widget selection
      setBatchType(initialBatchType);
    }
  }, [forceCurrentHoldings, visible, initialBatchType]);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [hasFileSelected, setHasFileSelected] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showPasteHint, setShowPasteHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const isProcessingPasteRef = useRef(false);
  const fileUploadRef = useRef<FileUpload>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const processData = useCallback(
    async (parsedData: CSVAllocationData[], source: string) => {
      setIsLoading(true);
      setError(null);
      setWarning(null);

      try {
        if (parsedData.length === 0) {
          throw new Error('No valid data found');
        }

        let validatedData = validateAndMarkRows(parsedData);

        // Check for duplicate ISINs
        const duplicateValidation = validateDuplicateISINs(validatedData);
        if (duplicateValidation.hasDuplicates) {
          validatedData = duplicateValidation.validatedData;
          const duplicateList = duplicateValidation.duplicateISINs.join(', ');
          setError(
            `Duplicate ISINs detected: ${duplicateList}. Each ISIN must appear only once. Please remove duplicates before proceeding.`,
          );
          setCsvData(validatedData);
          setFileName(source);
          return;
        }

        if (hasRowErrors(validatedData)) {
          setWarning(
            'Some rows have validation errors (highlighted in red). Please correct them before proceeding.',
          );
        }

        const allocationWarning = validateTotalAllocation(validatedData);
        if (allocationWarning) {
          if (allocationWarning.type === 'error') {
            setError(allocationWarning.message);
            setCsvData(validatedData);
            setFileName(source);
            return;
          } else {
            // Show warning as info message, don't prevent processing
            setWarning(allocationWarning.message);
          }
        }

        setFileName(source);
        setCsvData(validatedData);
        // Only set hasFileSelected to true if source is a file (has file extension)
        setHasFileSelected(source.includes('.'));
      } catch (err) {
        console.error('Data processing error:', err);
        setError(
          `Failed to process data: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
        setCsvData([]);
        setHasFileSelected(false);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const processFile = useCallback(
    async (file: File) => {
      setIsLoading(true);
      setError(null);
      setWarning(null);

      try {
        let parsedData: CSVAllocationData[];

        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (fileExtension === 'csv') {
          const text = await file.text();
          parsedData = parseCSV(text);
        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
          parsedData = await parseExcel(file);
        } else {
          throw new Error(
            'Unsupported file type. Please upload a CSV or Excel file.',
          );
        }

        await processData(parsedData, file.name);
        setHasFileSelected(true);
      } catch (err) {
        console.error('File parsing error:', err);
        setError(
          `Failed to parse file: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
        setCsvData([]);
        setHasFileSelected(false);
        setIsLoading(false);
      }
    },
    [processData],
  );

  const handleFileUpload = useCallback(
    async (event: { files: File[] }) => {
      const file = event.files[0];
      if (!file) return;
      await processFile(file);
    },
    [processFile],
  );

  const handlePaste = useCallback(
    async (e: ClipboardEvent) => {
      e.preventDefault();

      // Prevent multiple simultaneous paste operations
      if (isProcessingPasteRef.current) {
        return;
      }

      isProcessingPasteRef.current = true;

      try {
        const clipboardData = e.clipboardData;
        if (!clipboardData) {
          return;
        }

        // Try to get HTML data first (from Excel/Google Sheets)
        const htmlData = clipboardData.getData('text/html');
        const textData = clipboardData.getData('text/plain');

        let textToProcess = textData;

        // If we have HTML data, try to extract table data
        if (htmlData && htmlData.includes('<table')) {
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlData, 'text/html');
            const table = doc.querySelector('table');
            if (table) {
              const rows = Array.from(table.querySelectorAll('tr'));
              const tableData = rows.map((row) =>
                Array.from(row.querySelectorAll('td, th')).map(
                  (cell) => cell.textContent?.trim() || '',
                ),
              );
              textToProcess = tableData.map((row) => row.join('\t')).join('\n');
            }
          } catch (err) {
            console.warn(
              'Failed to parse HTML table data, falling back to plain text:',
              err,
            );
          }
        }

        if (!textToProcess || textToProcess.trim() === '') {
          setError(
            'No data found in clipboard. Please copy table data from Excel or another spreadsheet application.',
          );
          return;
        }

        try {
          const parsedData = parseTabularData(textToProcess);

          if (parsedData.length === 0) {
            setError(
              'No valid data found in pasted content. Please ensure you copied table data with headers.',
            );
            return;
          }

          await processData(parsedData, 'Pasted from Excel/Clipboard');
        } catch (err) {
          console.error('Paste parsing error:', err);
          setError(
            `Failed to parse pasted data: ${err instanceof Error ? err.message : 'Unknown error'}. Please ensure your data includes the required columns: isin, current_allocation. Additional columns are allowed but will be ignored.`,
          );
        }
      } finally {
        isProcessingPasteRef.current = false;
      }
    },
    [processData],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      // Handle file drops
      const file = e.dataTransfer.files[0];
      if (file) {
        await processFile(file);
        return;
      }

      // Handle text/table data drops
      const htmlData = e.dataTransfer.getData('text/html');
      const textData = e.dataTransfer.getData('text/plain');

      let textToProcess = textData;

      // If we have HTML data, try to extract table data
      if (htmlData && htmlData.includes('<table')) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlData, 'text/html');
          const table = doc.querySelector('table');
          if (table) {
            const rows = Array.from(table.querySelectorAll('tr'));
            const tableData = rows.map((row) =>
              Array.from(row.querySelectorAll('td, th')).map(
                (cell) => cell.textContent?.trim() || '',
              ),
            );
            textToProcess = tableData.map((row) => row.join('\t')).join('\n');
          }
        } catch (err) {
          console.warn(
            'Failed to parse HTML table data, falling back to plain text:',
            err,
          );
        }
      }

      if (textToProcess && textToProcess.trim() !== '') {
        try {
          const parsedData = parseTabularData(textToProcess);
          if (parsedData.length === 0) {
            setError(
              'No valid data found in dragged content. Please ensure you dragged table data with headers.',
            );
            return;
          }
          await processData(parsedData, 'Dragged from Excel/Table');
        } catch (err) {
          console.error('Drag parsing error:', err);
          setError(
            `Failed to parse dragged data: ${err instanceof Error ? err.message : 'Unknown error'}. Please ensure your data includes the required columns: isin, current_allocation. Additional columns are allowed but will be ignored.`,
          );
        }
      } else {
        setError(
          'No data found in drag operation. Please drag table data from Excel or another spreadsheet application.',
        );
      }
    },
    [processFile, processData],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handlePasteRef = useRef(handlePaste);
  handlePasteRef.current = handlePaste;

  useEffect(() => {
    if (!visible) return;

    const pasteHandler = (e: ClipboardEvent) => {
      handlePasteRef.current(e);
    };
    const keyDownHandler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        setShowPasteHint(true);
        setTimeout(() => setShowPasteHint(false), 2000);
      }
    };

    document.addEventListener('paste', pasteHandler);
    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('paste', pasteHandler);
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [visible]);

  const clearData = useCallback(() => {
    setCsvData([]);
    setFileName('');
    setDescription('');
    setBatchType('universe');
    setError(null);
    setWarning(null);
    setSubmissionError(null);
    isProcessingPasteRef.current = false;
    setHasFileSelected(false);
    setFileUploadKey((prev) => prev + 1);
  }, []);

  const formatSubmissionError = (error: unknown): string => {
    const isBQApiError = (
      err: unknown,
    ): err is {
      statusCode?: number;
      response?: {
        detail?: Array<{ msg?: string; input?: string; loc?: string[] }>;
      };
    } => typeof err === 'object' && err !== null && 'statusCode' in err;

    if (isBQApiError(error) && error.response?.detail) {
      const details = error.response.detail;

      const allocationErrors = details.filter((d) =>
        d.msg?.includes('Total allocation cannot exceed 100%'),
      );
      if (allocationErrors.length > 0) {
        return (
          allocationErrors[0].msg || 'Total allocation cannot exceed 100%.'
        );
      }

      const invalidIsins = details
        .filter((d) => d.loc?.includes('isin'))
        .map((d) => d.input)
        .filter(Boolean);
      if (invalidIsins.length > 0) {
        return `The following ISINs were rejected by the server:\n${invalidIsins.join('\n')}`;
      }

      const messages = details.map((d) => d.msg).filter(Boolean);
      if (messages.length > 0) {
        return messages.join('\n');
      }
    }

    if (error instanceof Error) return error.message;
    return 'Upload failed. Please try again.';
  };

  const handleProcessData = useCallback(async () => {
    if (hasRowErrors(csvData)) {
      setError(
        'Please fix all validation errors (rows highlighted in red) before creating snapshot.',
      );
      return;
    }

    const totalAllocationValidation = validateTotalAllocation(csvData);
    if (totalAllocationValidation?.type === 'error') {
      setError(totalAllocationValidation.message);
      return;
    }

    if (onDataProcessed && csvData.length > 0) {
      const cleanData = csvData.map(
        ({ _hasError, _errorMessage, ...rest }) => rest,
      );
      setSubmissionError(null);
      setIsSubmitting(true);
      try {
        onHide();
        await onDataProcessed(cleanData, description, batchType);
      } catch (err) {
        setSubmissionError(formatSubmissionError(err));
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [csvData, description, batchType, onDataProcessed, onHide]);

  const handleDialogHide = useCallback(() => {
    clearData();
    onHide();
  }, [clearData, onHide]);

  const hasErrors = hasRowErrors(csvData);
  const totalAllocationValidation = validateTotalAllocation(csvData);
  const hasTotalAllocationError = totalAllocationValidation?.type === 'error';

  return (
    <Dialog
      header="Upload or Paste Portfolio Data"
      visible={visible}
      onHide={handleDialogHide}
      style={{ width: '90vw', maxWidth: '1400px' }}
      modal
      dismissableMask
      maximizable
      blockScroll
      pt={{
        header: {
          style: {
            backgroundColor: 'var(--bq-surface-card)',
            color: 'var(--bq-text-primary)',
            borderBottom: '1px solid var(--bq-border-muted)',
          },
        },
        content: {
          style: {
            backgroundColor: 'var(--bq-surface-body)',
            color: 'var(--bq-text-primary)',
          },
        },
      }}
    >
      <div
        className="grid transition-colors"
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          minHeight: '400px',
          backgroundColor: isDragOver
            ? 'var(--bq-color-info-lighter)'
            : 'transparent',
        }}
      >
        {/* Compact Instructions */}
        <div className="col-12">
          <Card
            pt={{
              root: {
                style: {
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--bq-border-strong)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              },
              body: {
                style: {
                  backgroundColor: 'var(--bq-surface-card)',
                  padding: '0.75rem',
                },
              },
            }}
          >
            <div className="flex gap-6 align-items-start">
              <div style={{ minWidth: '280px', maxWidth: '280px' }}>
                <h4
                  className="my-0 mb-3 text-xs flex align-items-center gap-2"
                  style={{ color: 'var(--bq-text-primary)' }}
                >
                  <i
                    className="pi pi-info-circle text-sm"
                    style={{ color: 'var(--bq-color-info)' }}
                  ></i>
                  Example Format
                </h4>
                <div style={{ marginTop: '1rem' }}>
                  <ExampleTable />
                </div>
              </div>

              <div className="flex-1">
                <div
                  className="text-xs"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  <p
                    className="font-semibold mb-2 mt-0 text-sm"
                    style={{ color: 'var(--bq-text-primary)' }}
                  >
                    How to import:
                  </p>
                  <ul
                    className="ml-3 mb-2"
                    style={{
                      lineHeight: '1.6',
                      color: 'var(--bq-text-secondary)',
                    }}
                  >
                    <li>
                      <strong>Excel/Sheets:</strong> Copy cells (Ctrl+C) and
                      paste (Ctrl+V) or drag selected cells into the dialog
                    </li>
                    <li>
                      <strong>File:</strong> Upload CSV or Excel (.xlsx, .xls)
                      by dragging or choosing file
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex-1">
                <div
                  className="text-xs"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  <p
                    className="font-semibold mb-2 mt-0 text-sm"
                    style={{ color: 'var(--bq-text-primary)' }}
                  >
                    Requirements:
                  </p>
                  <ul
                    className="ml-3"
                    style={{
                      lineHeight: '1.6',
                      color: 'var(--bq-text-secondary)',
                    }}
                  >
                    <li>
                      Required:{' '}
                      <strong style={{ color: 'var(--bq-text-primary)' }}>
                        isin
                      </strong>
                      ,{' '}
                      <strong style={{ color: 'var(--bq-text-primary)' }}>
                        current_allocation
                      </strong>
                    </li>
                    <li>
                      Optional:{' '}
                      <strong style={{ color: 'var(--bq-text-primary)' }}>
                        sector
                      </strong>
                      ,{' '}
                      <strong style={{ color: 'var(--bq-text-primary)' }}>
                        ticker
                      </strong>
                      ,{' '}
                      <strong style={{ color: 'var(--bq-text-primary)' }}>
                        name
                      </strong>
                    </li>
                    <li>
                      Total allocation ≤ 100%, all values must be positive
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Batch Type Selection - Always Visible */}
        <div className="col-12">
          <Card
            pt={{
              root: {
                style: {
                  border: '1px solid var(--bq-border-strong)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              },
              body: {
                style: {
                  padding: '1rem',
                  backgroundColor: 'var(--bq-surface-card)',
                },
              },
            }}
          >
            <div>
              <label
                htmlFor="batch-type"
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--bq-text-primary)' }}
              >
                Allocation Batch Type
              </label>
              <Dropdown
                id="batch-type"
                value={batchType}
                onChange={(e) => {
                  if (!forceCurrentHoldings) {
                    setBatchType(e.value);
                  }
                }}
                options={[
                  { label: 'Universe', value: 'universe' },
                  { label: 'Benchmark', value: 'benchmark' },
                  { label: 'Current Holdings', value: 'current_holdings' },
                ]}
                className="w-full"
                placeholder="Select batch type"
                disabled={!!forceCurrentHoldings}
              />
              {!forceCurrentHoldings && (
                <div
                  className="text-xs mt-2"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  Select the type of allocation batch you are uploading.
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="col-12">
          <Card
            pt={{
              root: {
                style: {
                  padding: '0rem',
                  border: '1px solid var(--bq-border-strong)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              },
              body: {
                style: {
                  backgroundColor: 'var(--bq-surface-card)',
                },
              },
            }}
          >
            <div className="flex flex-column gap-3">
              <div className="text-center p-3">
                <i
                  className={`pi ${isDragOver ? 'pi-cloud-upload' : 'pi-table'} text-3xl mb-2`}
                  style={{
                    color: isDragOver
                      ? 'var(--bq-color-info)'
                      : 'var(--bq-text-secondary)',
                  }}
                ></i>
                <p
                  className="mb-2 font-semibold text-sm"
                  style={{ color: 'var(--bq-text-secondary)' }}
                >
                  {isDragOver
                    ? 'Drop anywhere!'
                    : 'Drag & Drop Data or Choose File'}
                </p>
                <div
                  className="text-xs p-2 rounded mb-2"
                  style={{
                    color: 'var(--bq-text-tertiary)',
                    backgroundColor: 'var(--bq-surface-muted)',
                  }}
                >
                  💡 Paste Excel data anywhere with Ctrl+V (Cmd+V on Mac)
                </div>
                <div className="flex gap-2 align-items-center justify-content-center mt-3">
                  {hasFileSelected ? (
                    <Button
                      icon="pi pi-times"
                      label="Clear Selection"
                      severity="secondary"
                      size="small"
                      onClick={clearData}
                      style={{
                        padding: '0.65625rem 1.09375rem',
                      }}
                    />
                  ) : (
                    <FileUpload
                      key={fileUploadKey}
                      ref={fileUploadRef}
                      mode="basic"
                      name="file"
                      accept=".csv,.xlsx,.xls"
                      maxFileSize={10000000}
                      customUpload={true}
                      uploadHandler={handleFileUpload}
                      auto
                      chooseLabel="Choose File"
                      pt={{
                        basicButton: {
                          style: {
                            padding: '0.65625rem 1.09375rem',
                          },
                        },
                      }}
                    />
                  )}
                  <Button
                    label="Download Template"
                    icon="pi pi-download"
                    size="small"
                    outlined
                    onClick={downloadCSVTemplate}
                    style={{
                      padding: '0.65625rem 1.09375rem',
                    }}
                  />
                </div>
              </div>

              {fileName && (
                <div className="flex align-items-center gap-2">
                  <i
                    className={`pi ${
                      fileName.includes('clipboard') ||
                      fileName.includes('Excel') ||
                      fileName.includes('Dragged')
                        ? 'pi-clone'
                        : fileName.endsWith('.csv')
                          ? 'pi-file'
                          : 'pi-file-excel'
                    }`}
                    style={{ color: 'var(--bq-color-info)' }}
                  ></i>
                  <span className="font-medium">{fileName}</span>
                  <Button
                    icon="pi pi-times"
                    text
                    severity="secondary"
                    size="small"
                    onClick={clearData}
                  />
                </div>
              )}

              {showPasteHint && (
                <Message
                  severity="info"
                  text="💡 Paste detected! Data will be processed automatically..."
                  className="animate-fade-in"
                />
              )}
              {error && <Message severity="error" text={error} />}
              {warning && <Message severity="info" text={warning} />}
            </div>
          </Card>
        </div>

        {csvData.length > 0 && (
          <div className="col-12">
            <Card
              pt={{
                root: {
                  style: {
                    border: '1px solid var(--bq-border-strong)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                },
                body: {
                  style: {
                    padding: '1rem',
                    backgroundColor: 'var(--bq-surface-card)',
                  },
                },
              }}
            >
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--bq-text-primary)' }}
                >
                  Description (optional)
                </label>
                <InputTextarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter a description for this portfolio analysis (optional)"
                  rows={3}
                  className="w-full"
                  autoResize
                />
                <div
                  className="text-xs mt-2"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  This description will be included with your portfolio analysis
                  submission.
                </div>
              </div>
            </Card>
          </div>
        )}

        {csvData.length > 0 && (
          <div className="col-12">
            <Card
              pt={{
                root: {
                  style: {
                    border: '1px solid var(--bq-border-strong)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                },
                header: {
                  style: {
                    backgroundColor: 'var(--bq-surface-card)',
                    borderBottom: '1px solid var(--bq-border-muted)',
                  },
                },
                body: {
                  style: {
                    backgroundColor: 'var(--bq-surface-card)',
                  },
                },
              }}
              header={
                <div
                  style={{ padding: '1.25rem' }}
                  className="flex justify-content-between align-items-center"
                >
                  <span>
                    Data Preview ({csvData.length} rows)
                    {(hasErrors || hasTotalAllocationError) && (
                      <span className="text-red-400 ml-2">
                        (⚠ Contains errors)
                      </span>
                    )}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      label={isSubmitting ? 'Uploading...' : 'Upload'}
                      icon={
                        isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'
                      }
                      severity="success"
                      onClick={handleProcessData}
                      disabled={
                        isSubmitting ||
                        csvData.length === 0 ||
                        hasErrors ||
                        hasTotalAllocationError
                      }
                      loading={isSubmitting}
                    />
                    <Button
                      label="Clear"
                      icon="pi pi-trash"
                      severity="secondary"
                      disabled={isSubmitting}
                      onClick={clearData}
                    />
                  </div>
                </div>
              }
            >
              {submissionError && (
                <div
                  className="p-3 mb-3 border-round"
                  style={{
                    backgroundColor: 'var(--red-900, #3b0a0a)',
                    border: '1px solid var(--red-600, #dc2626)',
                    color: 'var(--red-200, #fca5a5)',
                  }}
                >
                  <div className="flex align-items-center gap-2 mb-2">
                    <i
                      className="pi pi-exclamation-triangle"
                      style={{ color: 'var(--red-400, #f87171)' }}
                    />
                    <strong style={{ color: 'var(--red-300, #fca5a5)' }}>
                      Upload Failed
                    </strong>
                  </div>
                  <div style={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
                    {submissionError}
                  </div>
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-content-center align-items-center p-4">
                  <ProgressSpinner />
                  <span className="ml-2">Processing data...</span>
                </div>
              ) : (
                <CSVTable
                  data={csvData}
                  loading={false}
                  height="400px"
                  onDataChange={(updatedData: CSVAllocationData[]) =>
                    setCsvData(updatedData)
                  }
                />
              )}
            </Card>
          </div>
        )}
      </div>
    </Dialog>
  );
};
